import type { SpriteDefinition } from '@/render/spriteBaker';
import { PALETTE } from '@/sprites/palette';

/**
 * Terrain and props, 16x16.
 *
 * Ground tiles are generated from a deterministic hash rather than authored by
 * hand: they are texture, not shape, and hand-placing 256 speckles per tile is
 * a poor use of anyone's time. Props with actual silhouettes stay as ASCII.
 */

export const TILE_SIZE = 16;

/**
 * Cheap integer hash. Deterministic across runs and platforms, so the world
 * looks identical everywhere and in screenshots — Math.random would not.
 */
function hash(x: number, y: number, seed: number): number {
  let n = x * 374761393 + y * 668265263 + seed * 1274126177;
  n = (n ^ (n >>> 13)) >>> 0;
  n = Math.imul(n, 1274126177) >>> 0;
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

/** Speckled ground: `base` with `speck` scattered at `density`. */
function speckled(base: string, speck: string, seed: number, density = 0.18): string[] {
  const rows: string[] = [];
  for (let y = 0; y < TILE_SIZE; y++) {
    let row = '';
    for (let x = 0; x < TILE_SIZE; x++) {
      row += hash(x, y, seed) < density ? speck : base;
    }
    rows.push(row);
  }
  return rows;
}

/**
 * Ploughed soil: horizontal furrows with speckle between them.
 *
 * Parameterised by colour so wet soil is the identical pattern a few shades
 * darker — the player reads "watered" from the tone, not from a different
 * texture appearing under their feet.
 */
function furrowed(base: string, shadow: string): string[] {
  const rows: string[] = [];
  for (let y = 0; y < TILE_SIZE; y++) {
    let row = '';
    for (let x = 0; x < TILE_SIZE; x++) {
      const furrow = y % 4 === 0;
      row += furrow ? shadow : hash(x, y, 7) < 0.12 ? shadow : base;
    }
    rows.push(row);
  }
  return rows;
}

/** Interior flooring: horizontal boards with seams every four rows. */
function plank(): string[] {
  const rows: string[] = [];
  for (let y = 0; y < TILE_SIZE; y++) {
    let row = '';
    for (let x = 0; x < TILE_SIZE; x++) {
      const seam = y % 5 === 4;
      row += seam ? 'M' : hash(x, y, 13) < 0.1 ? 'M' : 'm';
    }
    rows.push(row);
  }
  return rows;
}

/** Roof shingles: banded rows with the notches offset band to band. */
function shingle(): string[] {
  const rows: string[] = [];
  for (let y = 0; y < TILE_SIZE; y++) {
    const band = Math.floor(y / 4);
    let row = '';
    for (let x = 0; x < TILE_SIZE; x++) {
      const notch = (x + band * 4) % 8 === 0;
      const bandEdge = y % 4 === 3;
      row += bandEdge || notch ? 'J' : 'j';
    }
    rows.push(row);
  }
  return rows;
}

/** House cladding: vertical boards, warmer than the interior flooring. */
function cladding(): string[] {
  const rows: string[] = [];
  for (let y = 0; y < TILE_SIZE; y++) {
    let row = '';
    for (let x = 0; x < TILE_SIZE; x++) {
      row += x % 6 === 5 ? 'F' : hash(x, y, 23) < 0.1 ? 'F' : 'f';
    }
    rows.push(row);
  }
  return rows;
}

const WINDOW = [
  'ffffffffffffffff',
  'fFFFFFFFFFFFFFFf',
  'fFMMMMMMMMMMMMFf',
  'fFMvvvvMvvvvvMFf',
  'fFMvvvvMvvvvvMFf',
  'fFMvvvvMvvvvvMFf',
  'fFMMMMMMMMMMMMFf',
  'fFMvvvvMvvvvVMFf',
  'fFMvvvvMvvvVVMFf',
  'fFMVVVVMVVVVVMFf',
  'fFMMMMMMMMMMMMFf',
  'fFFFFFFFFFFFFFFf',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
  'ffffffffffffffff',
];

/** River: horizontal current streaks, so the water reads as moving. */
function flowing(): string[] {
  const rows: string[] = [];
  for (let y = 0; y < TILE_SIZE; y++) {
    let row = '';
    for (let x = 0; x < TILE_SIZE; x++) {
      const streak = (y + Math.floor(x / 5)) % 4 === 0;
      row += streak ? '1' : hash(x, y, 51) < 0.14 ? '1' : 'K';
    }
    rows.push(row);
  }
  return rows;
}

/** Ocean: broad swell bands with the occasional fleck of foam. */
function swell(): string[] {
  const rows: string[] = [];
  for (let y = 0; y < TILE_SIZE; y++) {
    let row = '';
    for (let x = 0; x < TILE_SIZE; x++) {
      const trough = (y + Math.floor(Math.sin(x / 3) * 2)) % 6 === 0;
      const foam = hash(x, y, 61) < 0.05;
      row += foam ? '4' : trough ? '3' : '2';
    }
    rows.push(row);
  }
  return rows;
}

const TREE = [
  '.....nnnnnn.....',
  '...nnnnnnnnnn...',
  '..nnnnnnnnnnnn..',
  '.nnnnnnnnnnnnnn.',
  '.nnnnnnnnnnnnNN.',
  'nnnnnnnnnnnnnNNN',
  'nnnnnnnnnnnnnNNN',
  '.nnnnnnnnnnnNNN.',
  '.nnnnnnnnnnNNNN.',
  '..nnnnnnnnNNNN..',
  '...nnnnnnNNNN...',
  '.....nmmMM......',
  '......mmMM......',
  '......mmMM......',
  '.....mmmMMM.....',
  '....GGGGGGGG....',
];

const FENCE = [
  '................',
  '................',
  '..fff......fff..',
  '..fFF......fFF..',
  '..fFF......fFF..',
  'ffffffffffffffff',
  'FFFFFFFFFFFFFFFF',
  '..fFF......fFF..',
  '..fFF......fFF..',
  'ffffffffffffffff',
  'FFFFFFFFFFFFFFFF',
  '..fFF......fFF..',
  '..fFF......fFF..',
  '..fFF......fFF..',
  '..FFF......FFF..',
  '................',
];

const ROCK = [
  '................',
  '................',
  '.....rrrrr......',
  '....rrrrrrr.....',
  '...rrrrrrrrr....',
  '..rrrrrrrrrRR...',
  '..rrrrrrrrRRR...',
  '.rrrrrrrrrRRRR..',
  '.rrrrrrrrRRRRR..',
  '.rrrrrrrRRRRRR..',
  '..rrrrrRRRRRR...',
  '..rrrrRRRRRR....',
  '...RRRRRRRR.....',
  '....RRRRRR......',
  '................',
  '................',
];


const BED = [
  '..MMMMMMMMMMMM..',
  '..MWWWWWWWWWWM..',
  '..MWWWWWWWWWWM..',
  '..MqqqqqqqqqqM..',
  '..MqQQQQQQQQqM..',
  '..MqQQQQQQQQqM..',
  '..MqQQQQQQQQqM..',
  '..MqQQQQQQQQqM..',
  '..MqQQQQQQQQqM..',
  '..MqQQQQQQQQqM..',
  '..MqQQQQQQQQqM..',
  '..MqqqqqqqqqqM..',
  '..MMMMMMMMMMMM..',
  '................',
  '................',
  '................',
];

/**
 * A rug covers several tiles, so its pattern has to tile seamlessly. A motif
 * with its own border repeats that border on every tile and reads as a grid of
 * small rugs rather than one large one.
 */
function weave(): string[] {
  const rows: string[] = [];
  for (let y = 0; y < TILE_SIZE; y++) {
    let row = '';
    for (let x = 0; x < TILE_SIZE; x++) {
      const check = (Math.floor(x / 2) + Math.floor(y / 2)) % 2 === 0;
      row += check ? 'q' : 'Q';
    }
    rows.push(row);
  }
  return rows;
}

const DOORMAT = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '..MMMMMMMMMMMM..',
  '..MmmmmmmmmmmM..',
  '..MmMMmMMmMMmM..',
  '..MmmmmmmmmmmM..',
  '..MmMMmMMmMMmM..',
  '..MmmmmmmmmmmM..',
  '..MMMMMMMMMMMM..',
  '................',
  '................',
  '................',
  '................',
];

/** Concentric rings falling to black: a hole, read from directly above. */
const STAIRS_DOWN: string[] = [
  'rrrrrrrrrrrrrrrr',
  'rRRRRRRRRRRRRRRr',
  'rRrrrrrrrrrrrrRr',
  'rRrRRRRRRRRRRrRr',
  'rRrRrrrrrrrrRrRr',
  'rRrRrRRRRRRrRrRr',
  'rRrRrReeeeRrRrRr',
  'rRrRrReeeeRrRrRr',
  'rRrRrReeeeRrRrRr',
  'rRrRrReeeeRrRrRr',
  'rRrRrRRRRRRrRrRr',
  'rRrRrrrrrrrrRrRr',
  'rRrRRRRRRRRRRrRr',
  'rRrrrrrrrrrrrrRr',
  'rRRRRRRRRRRRRRRr',
  'rrrrrrrrrrrrrrrr',
];

/** The same shape lit from the middle, so up and down never read alike. */
const STAIRS_UP: string[] = [
  'rrrrrrrrrrrrrrrr',
  'rRRRRRRRRRRRRRRr',
  'rRwwwwwwwwwwwwRr',
  'rRwRRRRRRRRRRwRr',
  'rRwRwwwwwwwwRwRr',
  'rRwRwRRRRRRwRwRr',
  'rRwRwRwwwwRwRwRr',
  'rRwRwRwwwwRwRwRr',
  'rRwRwRwwwwRwRwRr',
  'rRwRwRwwwwRwRwRr',
  'rRwRwRRRRRRwRwRr',
  'rRwRwwwwwwwwRwRr',
  'rRwRRRRRRRRRRwRr',
  'rRwwwwwwwwwwwwRr',
  'rRRRRRRRRRRRRRRr',
  'rrrrrrrrrrrrrrrr',
];

/** A little covered cart: canopy, boards, a wheel. */
const CART: string[] = [
  '................',
  '.....wwwwww.....',
  '....wqqqqqqw....',
  '...wqqqqqqqqw...',
  '..wqqqqqqqqqqw..',
  '..mmmmmmmmmmmm..',
  '..mMMMMMMMMMMm..',
  '..mMiiiiiiiiMm..',
  '..mMiIIIIIIiMm..',
  '..mMiiiiiiiiMm..',
  '..mMMMMMMMMMMm..',
  '..mmmmmmmmmmmm..',
  '...M........M...',
  '..MMM......MMM..',
  '...M........M...',
  '................',
];

/** A notice board: two posts, a plank face, and paper pinned to it. */
const NOTICE_BOARD: string[] = [
  '................',
  '..mmmmmmmmmmmm..',
  '..mMMMMMMMMMMm..',
  '..mMwwwMMwwwMm..',
  '..mMwwwMMwwwMm..',
  '..mMMMMMMMMMMm..',
  '..mMwwwMMMMMMm..',
  '..mMwwwMMwwwMm..',
  '..mMMMMMMwwwMm..',
  '..mmmmmmmmmmmm..',
  '....M......M....',
  '....M......M....',
  '....M......M....',
  '...MMM....MMM...',
  '................',
  '................',
];

export const TILE_SPRITES = {
  grass: { name: 'tile-grass', rows: speckled('g', 'y', 1), palette: PALETTE },
  grassDark: { name: 'tile-grass-dark', rows: speckled('g', 'G', 2), palette: PALETTE },
  dirt: { name: 'tile-dirt', rows: speckled('t', 'T', 31, 0.2), palette: PALETTE },
  soil: { name: 'tile-soil', rows: furrowed('t', 'T'), palette: PALETTE },
  soilWet: { name: 'tile-soil-wet', rows: furrowed('T', 'O'), palette: PALETTE },
  path: { name: 'tile-path', rows: speckled('p', 'P', 3, 0.22), palette: PALETTE },
  water: { name: 'tile-water', rows: speckled('a', 'A', 4, 0.25), palette: PALETTE },
  waterLake: { name: 'tile-water-lake', rows: speckled('E', 'Y', 41, 0.3), palette: PALETTE },
  waterRiver: { name: 'tile-water-river', rows: flowing(), palette: PALETTE },
  waterOcean: { name: 'tile-water-ocean', rows: swell(), palette: PALETTE },
  sand: { name: 'tile-sand', rows: speckled('5', '6', 43, 0.22), palette: PALETTE },
  wetSand: { name: 'tile-wet-sand', rows: speckled('6', '5', 44, 0.18), palette: PALETTE },
  tree: { name: 'prop-tree', rows: TREE, palette: PALETTE },
  fence: { name: 'prop-fence', rows: FENCE, palette: PALETTE },
  rock: { name: 'prop-rock', rows: ROCK, palette: PALETTE },
  wall: { name: 'tile-wall', rows: speckled('x', 'X', 11, 0.16), palette: PALETTE },
  roof: { name: 'tile-roof', rows: shingle(), palette: PALETTE },
  cladding: { name: 'tile-cladding', rows: cladding(), palette: PALETTE },
  window: { name: 'tile-window', rows: WINDOW, palette: PALETTE },
  floor: { name: 'tile-floor', rows: plank(), palette: PALETTE },
  rug: { name: 'prop-rug', rows: weave(), palette: PALETTE },
  bed: { name: 'prop-bed', rows: BED, palette: PALETTE },
  doormat: { name: 'prop-doormat', rows: DOORMAT, palette: PALETTE },

  // Underground. Walls are the darker stone so the floor reads as the space
  // you can move through, which is the opposite of the cottage above ground.
  dungeonWall: { name: 'tile-dungeon-wall', rows: speckled('R', 'e', 23, 0.14), palette: PALETTE },
  dungeonFloor: { name: 'tile-dungeon-floor', rows: speckled('r', 'R', 29, 0.12), palette: PALETTE },
  stairsDown: { name: 'tile-stairs-down', rows: STAIRS_DOWN, palette: PALETTE },
  stairsUp: { name: 'tile-stairs-up', rows: STAIRS_UP, palette: PALETTE },
  cart: { name: 'tile-cart', rows: CART, palette: PALETTE },
  noticeBoard: { name: 'tile-notice-board', rows: NOTICE_BOARD, palette: PALETTE },
} as const satisfies Record<string, SpriteDefinition>;

export type TileSpriteKey = keyof typeof TILE_SPRITES;
